import { UIDateElement } from './ui-dateelement';
import { updateElement } from '../../global/ui-helpers';
import { Digest } from '../../global/digest';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr, Optional } from '../../global/utils.type';
import type { UIMonthPicker } from './ui-monthpicker';
import type { UIField } from '../field/ui-field';
import styles from './ui-monthrange.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UIMonthRange
 * @element ui-monthrange
 * @classdesc Represents a class for <code>ui-monthrange</code> element.
 * Combines two ui-monthpicker to select month range.
 * @property {string} [controlId] {@attr control-id} Base id for the month range group and its monthpickers.
 *
 * Host element id resolution:
 * - If you set id directly on <ui-monthrange>, it is preserved and used as the base for deriving child ids.
 *   Note: This only affects inputs that don't already have a user-set id. Existing user-set input ids
 *   are never overwritten.
 * - If no host id is set and control-id is provided, the host gets control-id.
 * - If no host id is set and no control-id is provided, a base id is generated.
 *
 * Child monthpicker input id resolution:
 * - User-set ids are never overwritten. If you set an id on an input (e.g. id="monthrange-from"),
 *   that value is preserved, regardless of whether a host id or control-id is set.
 * - If an input has no id, a derived id is applied: first picker `${baseId}-from`, second `${baseId}-to`,
 *   where baseId is the host's id (user-set, control-id, or generated).
 * - The group element always gets an id (user-set, control-id, or generated) so that aria-labelledby
 *   works correctly when the component is used inside a ui-field.
 * @property {UIField} control Shortcut for control.
 * @property {UIMonthPicker} pickerFrom Shortcut for monthpicker-from element.
 * @property {UIMonthPicker} pickerTo Shortcut for monthpicker-to element.
 * @slot
 * @example
 * <ui-monthrange min="2017-09" max="2020-11">
 *   <ui-monthpicker>
 *     <input type="text" value="2018-03" name="period-from">
 *   </ui-monthpicker>
 *   <ui-monthpicker>
 *     <input type="text" value="2018-06" name="period-to">
 *   </ui-monthpicker>
 * </ui-monthrange>
 */
@Component({ tag: 'ui-monthrange', styles })
export class UIMonthRange extends UIDateElement {
    @Attribute(String) controlId?: string;

    @Child('ui-monthpicker.-from') pickerFrom?: UIMonthPicker;
    @Child('ui-monthpicker.-to') pickerTo?: UIMonthPicker;

    private _fromInputHadUserId = false;
    private _toInputHadUserId = false;

    /**
     * @type {UIMonthRange}
     */
    override readonly control: this = this;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['control-id'];
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        _oldValue: AttributeValue,
        newValue: Optional<string>
    ): void {
        switch (name) {
            case 'control-id':
                if (this.pickerFrom) {
                    if (newValue) {
                        this.pickerFrom.controlId = newValue;
                    } else {
                        this.pickerFrom.removeAttribute('control-id');
                    }
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Checks if an id is a generated id (empty or matches Digest.randomId() pattern)
     * @param {string|null|undefined} id
     * @returns {boolean}
     * @private
     */
    private isGeneratedId(id: Optional<string>): boolean {
        return !id || /^uid-[0-9a-f]{8}$/i.test(id);
    }

    /**
     * Type of input in this case only 'month'.
     * @type {string}
     * @readonly
     */
    override get valueType(): string {
        return UIDateElement.VALUE_TYPES.MONTH;
    }

    /**
     * @type {number}
     * @readonly
     */
    get tsFrom(): number {
        return this.convertPickerValueToTs(this.pickerFrom);
    }

    /**
     * @type {number}
     * @readonly
     */
    get tsTo(): number {
        return this.convertPickerValueToTs(this.pickerTo);
    }

    /**
     * @param {UIMonthPicker} picker
     * @returns {number}
     * @private
     */
    private convertPickerValueToTs(picker?: UIMonthPicker): number {
        return new Date(`${picker?.value || ''}-01`).getTime();
    }

    /**
     * Fires callback when a value of the date-from control is changed.
     * @param {Event} e
     * @private
     */
    private handleChangeDateFrom = (e?: Event | CustomEvent<unknown>): void => {
        this.syncControls();
        const ts = this.convertPickerValueToTs(e?.target as UIMonthPicker);
        if (ts > this.tsTo && this.pickerTo && this.pickerFrom) {
            this.pickerTo.value = this.pickerFrom.value;
        }
    };

    /**
     * Fires callback when a value of the date-to control is changed.
     * @param {Event} e
     * @private
     */
    private handleChangeDateTo = (e?: Event | CustomEvent<unknown>): void => {
        this.syncControls();
        const ts = this.convertPickerValueToTs(e?.target as UIMonthPicker);
        if (ts < this.tsFrom && this.pickerFrom && this.pickerTo) {
            this.pickerFrom.value = this.pickerTo.value;
        }
    };

    /**
     * Returns the control id that should be used by ui-field for label association.
     * @returns {string}
     */
    getControlIdForField(): string {
        const existingId = this.getAttribute('id') || this.id;

        if (existingId?.trim() && !this.isGeneratedId(existingId)) {
            return existingId;
        }

        if (this.controlId) {
            this.id = this.controlId;
            this.setAttribute('id', this.controlId);
            return this.controlId;
        }

        const baseId = Digest.randomId();
        this.id = baseId;
        this.setAttribute('id', baseId);
        return baseId;
    }

    /**
     * Sets the relationship between the label of an ancestor ui-field and the host.
     * @private
     */
    private updateId(): void {
        const field = this.closest<UIField>('ui-field');
        const labelEl = field?.querySelector<HTMLElement>(
            '.ui-field__label-inner'
        );

        if (!field || !labelEl) {
            return;
        }

        const groupId = this.getControlIdForField();

        if (!labelEl.id && groupId) {
            labelEl.setAttribute('id', `${groupId}-label`);
        }
        this.setAttribute('role', 'group');
        const labelledBy = labelEl.id || (groupId ? `${groupId}-label` : null);
        if (labelledBy) {
            this.setAttribute('aria-labelledby', labelledBy);
        }
    }

    /**
     * Sets the ids for the monthpicker inputs.
     * @param {string} baseId
     * @private
     */
    private setPickerFromToIds(baseId: string): void {
        if (this.pickerFrom?.control && !this._fromInputHadUserId) {
            this.pickerFrom.control.setAttribute('id', `${baseId}-from`);
        }
        if (this.pickerTo?.control && !this._toInputHadUserId) {
            this.pickerTo.control.setAttribute('id', `${baseId}-to`);
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const pickers = this.queryChildren('ui-monthpicker');
        const pickerInputs: Array<NullOr<HTMLInputElement>> = [
            pickers[0]
                ? pickers[0].querySelector<HTMLInputElement>('input')
                : null,
            pickers[1]
                ? pickers[1].querySelector<HTMLInputElement>('input')
                : null,
        ];

        // Check if inputs have user-set ids
        const fromId = pickerInputs[0]?.getAttribute('id');
        const toId = pickerInputs[1]?.getAttribute('id');
        this._fromInputHadUserId = !!(
            pickerInputs[0] &&
            fromId?.trim() &&
            !this.isGeneratedId(fromId)
        );
        this._toInputHadUserId = !!(
            pickerInputs[1] &&
            toId?.trim() &&
            !this.isGeneratedId(toId)
        );

        // Check if host has a user-set id
        const hostId = this.getAttribute('id') || this.id;
        const hostHadUserId = !!(hostId?.trim() && !this.isGeneratedId(hostId));

        // Resolve base id for deriving monthpicker input ids
        // If host has a user-set id, use it; otherwise use control-id if provided, or generate one
        const baseId = hostHadUserId
            ? hostId
            : this.controlId || Digest.randomId();

        if (!hostHadUserId) {
            this.id = baseId;
            this.setAttribute('id', baseId);
        }

        this.insertElements([
            {
                tagName: 'ui-monthpicker',
                element: pickers[0],
                classList: { '-from': true },
                children: [
                    {
                        tagName: 'input',
                        element: pickerInputs[0],
                        attributes: {
                            ...((!pickerInputs[0] ||
                                !this._fromInputHadUserId) && {
                                id: `${baseId}-from`,
                            }),
                            min: this.min,
                            max: this.max,
                        },
                    },
                ],
            },
            {
                tagName: 'span',
                attributes: {
                    class: 'ui-monthrange__separator',
                },
            },
            {
                tagName: 'ui-monthpicker',
                element: pickers[1],
                classList: { '-to': true },
                children: [
                    {
                        tagName: 'input',
                        element: pickerInputs[1],
                        attributes: {
                            ...((!pickerInputs[1] ||
                                !this._toInputHadUserId) && {
                                id: `${baseId}-to`,
                            }),
                            min: this.min,
                            max: this.max,
                        },
                    },
                ],
            },
        ]);

        // Set derived ids for existing inputs that don't have user-set ids
        this.setPickerFromToIds(baseId);

        this.syncControls();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        // Apply derived ids only when input did not have a user-set id
        const baseId = this.getControlIdForField();
        this.setPickerFromToIds(baseId);
        this.updateId();

        if (this.pickerFrom) {
            updateElement(this.pickerFrom, {
                events: {
                    change: this.handleChangeDateFrom,
                },
            });
        }

        if (this.pickerTo) {
            updateElement(this.pickerTo, {
                events: {
                    change: this.handleChangeDateTo,
                },
            });
        }
    }
}
